import React, { useContext, useMemo, useState } from "react";
import { Button, Input, Label, Table } from "reactstrap";
import ExportJsonExcel from "js-export-excel";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import { Pagination, Search } from "../../DataTable";
import RevalidacionModal from "./RevalidacionModal";
import useRevalidaciones, { RANGO_DEFAULT, PRESETS_ETA } from "./useRevalidaciones";
import { ESTATUS_COBRO, calculaTotales, fmtUSD, fmtMXN, fmtNum, colorEstatus } from "./revalidacionUtils";

const ITEMS_PER_PAGE = 50;
const colorCobro = (p) => (p === "COBRADO" ? "success" : p === "FACTURADO" ? "info" : p === "PENDIENTE" ? "warning" : "secondary");
const corto = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e6) return "$" + (n / 1e6).toFixed(1) + "M";
  if (Math.abs(n) >= 1e4) return "$" + (n / 1e3).toFixed(0) + "K";
  return fmtUSD(n);
};

export default function RevalidacionesCobros() {
  const { user } = useContext(AuthContext);
  const [inicio, setInicio] = useState(RANGO_DEFAULT.inicio);
  const [fin, setFin] = useState(RANGO_DEFAULT.fin);
  const { filas, cargando, actualizaFila } = useRevalidaciones(inicio, fin);

  const [busca, setBusca] = useState("");
  const [fCliente, setFCliente] = useState("");
  const [fNaviera, setFNaviera] = useState("");
  const [fCobro, setFCobro] = useState("");
  const [soloConCargos, setSoloConCargos] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [abierto, setAbierto] = useState(null);

  const puedeEditar = !!user.menuAdmin;
  const opciones = (campo) => [...new Set(filas.map((f) => f.contenedor[campo]).filter(Boolean))].sort();

  const enriquecidas = useMemo(
    () => filas.map((f) => { const r = f.revalidacion || {}; return { ...f, r, t: calculaTotales(r) }; }),
    [filas]
  );

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return enriquecidas
      .filter((f) => !soloConCargos || f.t.granTotal > 0 || f.r.estatus)
      .filter((f) => !fCliente || f.contenedor.cliente === fCliente)
      .filter((f) => !fNaviera || f.contenedor.naviera === fNaviera)
      .filter((f) => !fCobro || (fCobro === "SIN" ? !f.r.pagado : f.r.pagado === fCobro))
      .filter((f) => !q || [f.contenedor.referencia, f.contenedor.numeroReferencia, f.contenedor.cliente, f.contenedor.naviera, f.r.facturaNaviera, f.r.bl]
        .some((v) => String(v || "").toLowerCase().includes(q)))
      .sort((a, b) => String(a.contenedor.eta).localeCompare(String(b.contenedor.eta)));
  }, [enriquecidas, busca, fCliente, fNaviera, fCobro, soloConCargos]);

  const kpi = useMemo(() => {
    const k = { facturable: 0, cobrado: 0, porCobrar: 0, extras: 0, demoras: 0, garantias: 0, mxn: 0 };
    for (const f of filtradas) {
      k.facturable += f.t.granTotal;
      if (f.r.pagado === "COBRADO") k.cobrado += f.t.totalIVA; else k.porCobrar += f.t.totalIVA;
      k.extras += f.t.extrasPorCobrar;
      if (f.r.estatusDemoras !== "PAGADO") k.demoras += f.t.demorasIVA;
      k.garantias += Number(f.r.garantia) || 0;
      k.mxn += f.t.totalMXN;
    }
    return k;
  }, [filtradas]);

  const paginaFilas = filtradas.slice((pagina - 1) * ITEMS_PER_PAGE, pagina * ITEMS_PER_PAGE);
  const reset = (fn) => (e) => { fn(e.target.value); setPagina(1); };
  const preset = (p) => { const [a, b] = p.rango(); setInicio(a.format("YYYY-MM-DD")); setFin(b.format("YYYY-MM-DD")); setPagina(1); };

  const excel = () => {
    const datos = filtradas.map((f) => ({
      Referencia: f.contenedor.numeroReferencia, Contenedor: f.contenedor.referencia, Cliente: f.contenedor.cliente,
      Naviera: f.contenedor.naviera, MBL: f.r.bl || "", ETA: f.contenedor.eta, CargosLocales: f.r.cargosLocales || 0,
      ServicioML: f.r.servicioML || 0, TotalUSD: f.t.total, TotalIVA: f.t.totalIVA, TC: f.r.tipoCambio || "",
      TotalMXN: f.t.totalMXN || "", Garantia: f.r.garantia || "", FechaGarantia: f.r.fechaGarantia || "",
      CargosExtra: f.t.totalExtras, Demoras: f.t.demorasIVA, EstatusDemoras: f.r.estatusDemoras || "",
      GranTotal: f.t.granTotal, EstatusCobro: f.r.pagado || "", FacturaNaviera: f.r.facturaNaviera || "", Notas: f.r.notasCobro || "",
    }));
    const cols = Object.keys(datos[0] || { Referencia: "" });
    new ExportJsonExcel({ fileName: "RevalidacionesCobros", datas: [{ sheetData: datos, sheetName: "Cobros", sheetFilter: cols, sheetHeader: cols }] }).saveExcel();
  };

  if (!user.menuAdmin) return <Header />;

  return (
    <>
      <Header />
      <div className="card container col-sm-11 rc-page">
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <span className="page-title-icon"><i className="fas fa-file-invoice-dollar" /></span>
              Revalidaciones · Cargos y cobros
            </h3>
            <p className="page-subtitle">Cargos locales, servicio ML, cargos extra, demoras y garantías por contenedor</p>
          </div>
          <div className="page-actions">
            <Button size="sm" color="primary" href="/Revalidaciones"><i className="fas fa-stamp" /> Datos de revalidación</Button>
            <Button size="sm" color="secondary" href="/MenuContenedores"><i className="fas fa-arrow-left" /> Regresar</Button>
            <Button size="sm" onClick={excel} disabled={!filtradas.length}><i className="far fa-file-excel" /> Excel</Button>
          </div>
        </div>

        <div className="rc-presets">
          {PRESETS_ETA.map((p) => {
            const [a, b] = p.rango();
            const activo = a.format("YYYY-MM-DD") === inicio && b.format("YYYY-MM-DD") === fin;
            return <button key={p.label} type="button" className={`rc-preset${activo ? " active" : ""}`} onClick={() => preset(p)}>{p.label}</button>;
          })}
        </div>
        <div className="filter-bar">
          <div><Label>ETA desde</Label><Input bsSize="sm" type="date" value={inicio} onChange={reset(setInicio)} /></div>
          <div><Label>ETA hasta</Label><Input bsSize="sm" type="date" value={fin} onChange={reset(setFin)} /></div>
          <div>
            <Label>Cliente</Label>
            <Input bsSize="sm" type="select" value={fCliente} onChange={reset(setFCliente)}>
              <option value="">Todos</option>{opciones("cliente").map((x) => <option key={x}>{x}</option>)}
            </Input>
          </div>
          <div>
            <Label>Naviera</Label>
            <Input bsSize="sm" type="select" value={fNaviera} onChange={reset(setFNaviera)}>
              <option value="">Todas</option>{opciones("naviera").map((x) => <option key={x}>{x}</option>)}
            </Input>
          </div>
          <div>
            <Label>Estatus de cobro</Label>
            <Input bsSize="sm" type="select" value={fCobro} onChange={reset(setFCobro)}>
              <option value="">Todos</option><option value="SIN">Sin estatus</option>
              {ESTATUS_COBRO.filter(Boolean).map((x) => <option key={x}>{x}</option>)}
            </Input>
          </div>
          <div style={{ justifyContent: "flex-end" }}>
            <Label check style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={soloConCargos} onChange={(e) => { setSoloConCargos(e.target.checked); setPagina(1); }} /> Solo con captura
            </Label>
          </div>
        </div>

        <div className={`rc-kpis${cargando ? " rc-loading" : ""}`}>
          {[
            { label: "Total facturable (IVA)", valor: corto(kpi.facturable), titulo: fmtUSD(kpi.facturable), icon: "fas fa-file-invoice-dollar", bg: "#dcfce7", fg: "#15803d", sub: `${fmtNum(filtradas.length)} contenedores` },
            { label: "Cargos cobrados", valor: corto(kpi.cobrado), titulo: fmtUSD(kpi.cobrado), icon: "fas fa-hand-holding-usd", bg: "#cffafe", fg: "#0e7490", sub: "Estatus COBRADO" },
            { label: "Cargos por cobrar", valor: corto(kpi.porCobrar), titulo: fmtUSD(kpi.porCobrar), icon: "fas fa-balance-scale", bg: "#fee2e2", fg: "#b91c1c", sub: kpi.mxn ? `${fmtMXN(kpi.mxn)} MXN con TC` : "Captura TC para ver MXN" },
            { label: "Cargos extra por cobrar", valor: corto(kpi.extras), titulo: fmtUSD(kpi.extras), icon: "fas fa-plus-circle", bg: "#ffedd5", fg: "#c2410c", sub: "Con IVA" },
            { label: "Demoras por cobrar", valor: corto(kpi.demoras), titulo: fmtUSD(kpi.demoras), icon: "fas fa-hourglass-half", bg: "#fef3c7", fg: "#b45309", sub: "Sin estatus PAGADO" },
            { label: "Garantías", valor: corto(kpi.garantias), titulo: fmtUSD(kpi.garantias), icon: "fas fa-shield-alt", bg: "#ede9fe", fg: "#6d28d9", sub: "Depósitos registrados" },
          ].map((k) => (
            <div key={k.label} className="rc-kpi">
              <div className="rc-kpi-icon" style={{ background: k.bg, color: k.fg }}><i className={k.icon} /></div>
              <div className="rc-kpi-body">
                <div className="rc-kpi-label">{k.label}</div>
                <div className="rc-kpi-value" title={k.titulo}>{k.valor}</div>
                <div className="rc-kpi-sub">{k.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="table-toolbar">
          <Pagination total={filtradas.length} itemsPerPage={ITEMS_PER_PAGE} currentPage={pagina} onPageChange={setPagina} />
          <Search onSearch={(v) => { setBusca(v); setPagina(1); }} />
        </div>

        {cargando ? (
          <div className="rc-state"><i className="fas fa-spinner fa-spin" /><div>Cargando contenedores…</div></div>
        ) : filtradas.length === 0 ? (
          <div className="rc-state">
            <i className="fas fa-box-open" />
            <div>{soloConCargos ? "Ningún contenedor del periodo tiene cargos capturados todavía" : "No hay contenedores con estos filtros"}</div>
            {soloConCargos ? <Button size="sm" color="secondary" onClick={() => setSoloConCargos(false)}>Ver todos los contenedores</Button> : null}
          </div>
        ) : (
          <Table size="sm" hover className="table-responsive-xl">
            <thead>
              <tr>
                <th>Referencia</th><th>Contenedor</th><th>Cliente</th><th>Naviera</th><th>ETA</th>
                <th className="td-num">Cargos locales</th><th className="td-num">Servicio ML</th><th className="td-num">Total IVA</th>
                <th className="td-num">TC</th><th className="td-num">Total MXN</th><th className="td-num">Extras</th>
                <th className="td-num">Demoras</th><th className="td-num">Gran total</th><th>Cobro</th><th>Revalidación</th><th></th>
              </tr>
            </thead>
            <tbody>
              {paginaFilas.map((f) => (
                <tr key={f.contenedor._id} className="rv-row" onClick={() => setAbierto(f.contenedor._id)}>
                  <td className="td-strong">{f.contenedor.numeroReferencia}</td>
                  <td className="td-strong">{f.contenedor.referencia}</td>
                  <td>{f.contenedor.cliente}</td>
                  <td>{f.contenedor.naviera}</td>
                  <td>{f.contenedor.eta || "—"}</td>
                  <td className="td-num td-money">{fmtUSD(f.r.cargosLocales)}</td>
                  <td className="td-num td-money">{fmtUSD(f.r.servicioML)}</td>
                  <td className="td-num td-money">{fmtUSD(f.t.totalIVA)}</td>
                  <td className="td-num">{f.r.tipoCambio ? fmtNum(f.r.tipoCambio, 2) : <span className="td-muted">—</span>}</td>
                  <td className="td-num td-money">{f.t.totalMXN ? fmtMXN(f.t.totalMXN) : <span className="td-muted">—</span>}</td>
                  <td className="td-num td-money">
                    {f.t.totalExtras ? fmtUSD(f.t.totalExtras) : <span className="td-muted">—</span>}
                    {f.t.extrasPorCobrar ? <div className="rv-sub-rojo">{fmtUSD(f.t.extrasPorCobrar)} por cobrar</div> : null}
                  </td>
                  <td className="td-num td-money">
                    {f.t.demorasIVA ? fmtUSD(f.t.demorasIVA) : <span className="td-muted">—</span>}
                    {f.r.estatusDemoras ? <div className="rv-sub">{f.r.estatusDemoras}</div> : null}
                  </td>
                  <td className="td-num td-money"><strong>{fmtUSD(f.t.granTotal)}</strong></td>
                  <td><span className={`badge-soft badge-soft-${colorCobro(f.r.pagado)}`}>{f.r.pagado || "—"}</span></td>
                  <td><span className={`badge-soft badge-soft-${colorEstatus(f.estatus)}`}>{f.estatus || "Sin iniciar"}</span></td>
                  <td>
                    <Button size="sm" color="info" title={puedeEditar ? "Editar cobros" : "Ver"} onClick={(e) => { e.stopPropagation(); setAbierto(f.contenedor._id); }}>
                      <i className={puedeEditar ? "fas fa-edit" : "fas fa-eye"} />
                    </Button>
                  </td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan={7} className="negrita" align="right">TOTAL ({fmtNum(filtradas.length)})</td>
                <td className="negrita td-num td-money">{fmtUSD(filtradas.reduce((s, f) => s + f.t.totalIVA, 0))}</td>
                <td></td>
                <td className="negrita td-num td-money">{fmtMXN(kpi.mxn)}</td>
                <td className="negrita td-num td-money">{fmtUSD(filtradas.reduce((s, f) => s + f.t.totalExtras, 0))}</td>
                <td className="negrita td-num td-money">{fmtUSD(filtradas.reduce((s, f) => s + f.t.demorasIVA, 0))}</td>
                <td className="negrita td-num td-money">{fmtUSD(kpi.facturable)}</td>
                <td colSpan={3}></td>
              </tr>
            </tbody>
          </Table>
        )}
      </div>

      <RevalidacionModal
        isOpen={!!abierto}
        toggle={() => setAbierto(null)}
        contenedorId={abierto}
        modo="cobros"
        puedeEditar={puedeEditar}
        puedeSubir={!!user.contenedoresCreate}
        onSaved={actualizaFila}
      />
    </>
  );
}
